import dayjs from 'dayjs';

import type { PagesToReviewTab } from './api/discoverabilityPagesApi';

// The row terminator the export endpoint writes (RFC-4180). Kept here so the
// row count and the endpoint agree on what a line is.
const ROW_TERMINATOR = '\r\n';

export type PagesToReviewExportFilenameInput = {
  tab: PagesToReviewTab;
  /** The applied single-domain filter, or undefined for every domain. */
  domain: string | undefined;
  dateTimeFrom: string;
  dateTimeTo: string;
};

// `shop.example.com` → `shop-example-com`: dots would read as a second file
// extension, and anything outside the safe set is not worth carrying into a
// filename the OS has to accept.
function filenameSafe(value: string): string {
  return value.replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-|-$/g, '');
}

// The user's own day, not the UTC day. The window instants come from local day
// boundaries and the modal states the window in local time, so slicing the UTC
// date out of the ISO string would name the file a day earlier than the modal
// just promised for anyone east of UTC.
function dayOf(instant: string): string {
  return dayjs(instant).format('YYYY-MM-DD');
}

const ALL_DOMAINS = 'all';

/**
 * The saved file's name. The endpoint sends no `Content-Disposition` — the
 * dashboard fetches the body and saves it, so naming is the client's job, which
 * is what lets the name carry the scope and the effective window.
 */
export function pagesToReviewExportFilename({
  tab,
  domain,
  dateTimeFrom,
  dateTimeTo,
}: PagesToReviewExportFilenameInput): string {
  const scope = filenameSafe(tab);
  const domainPart = domain === undefined ? ALL_DOMAINS : filenameSafe(domain);

  return `pages-to-review-${scope}-${domainPart}-${dayOf(dateTimeFrom)}-${dayOf(
    dateTimeTo
  )}.csv`;
}

/**
 * How many pages the export actually carries. The response always opens with a
 * header row, so a header-only body means the chosen scope matched nothing —
 * which the modal reports instead of saving an empty file.
 */
export function exportedDataRowCount(csv: string): number {
  return csv
    .split(ROW_TERMINATOR)
    .filter((line) => line.length > 0)
    .slice(1).length;
}
